import { combineReducers } from 'redux';
import { mergeSchedules, splitInterval, overlaps } from '../../../utils/schedules';
import * as types from './types';
import { uniqWith, isEqual } from 'lodash';
import moment from 'moment';

const initialState = {
  loading: false,
  membership: {},
  group: {},
  team: {},
  task: {},
  scheduleWarnings: {}
};

// Validates data staleness by checking whether any interval ends after the current time
// Note: if the dashboard is locked to a specific time, this check will always return true
const checkStateStaleness = state => {
  const membershipEndTimes = Object.values(state.membership).map(m => moment(m.end_time));
  const taskEndTimes = Object.values(state.task).map(t => moment(t.end_time));
  const teamEndTimes = Object.values(state.team).map(t => moment(t.end_time));
  const maxMembershipEndTime = moment.max(membershipEndTimes);
  const maxTaskEndTime = moment.max(taskEndTimes);
  const maxTeamEndTime = moment.max(teamEndTimes);
  const now = moment(); // Will this break when locking an incident in the past?
  return maxMembershipEndTime.isBefore(now) || maxTaskEndTime.isBefore(now) || maxTeamEndTime.isBefore(now);
};

// Received a bulk update, which contains a list of normal
// COMBINED_SCHEDULE_UPDATED actions. Apply these one by one, but only the
// unique ones
const batchUpdate = (state, action) =>
  uniqWith(action.payload.actions, isEqual).reduce((currentState, p) => fullScheduleReducer(currentState, p), state);

export const fullScheduleReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.COMBINED_SCHEDULE_UPDATED: {
      const ownerType = action.payload.ownerType;
      // ignore updates about entity types we don't care about, such as 'station'
      if (!state[ownerType]) {
        return state;
      }

      const oldSchedule = state[ownerType][action.payload.id];
      const newSchedule = action.payload.schedule;
      const mergeMode = action.mergeMode;
      const overwriteUnsavedChanges = action.overwriteUnsavedChanges;

      // if there are any unsaved changes in this entity's schedule, don't do
      // any updates. This is to avoid situations in e.g. the duty planner
      // where:
      // 1. the user makes a change and saves it
      // 2. the user makes more changes
      // 3. the websocket update from #1 comes in and overwrites #2
      const intervals = oldSchedule?.intervals || [];
      if (!overwriteUnsavedChanges && intervals.some(i => i.transient)) return state;

      return {
        ...state,
        loading: false,
        [ownerType]: {
          ...state[ownerType],
          [action.payload.id]: mergeSchedules(oldSchedule, newSchedule, mergeMode)
        }
      };
    }
    case types.BATCH_COMBINED_SCHEDULE_UPDATED: {
      return batchUpdate(state, action);
    }
    case types.SPLIT_COMBINED_SCHEDULE_INTERVAL: {
      const { ownerType, ownerId, time } = action.payload;
      const schedule = state[ownerType][ownerId];
      return {
        ...state,
        [ownerType]: {
          ...state[ownerType],
          [ownerId]: splitInterval(schedule, time)
        }
      };
    }
    case types.RESET_STATE: {
      return {
        ...initialState
      };
    }
    case types.ERROR: {
      // we still want to leave existing data intact as well as "last fetch"
      // which would let us determine if the data is stale or not
      const errorTimes = [...(state.errorTimes || []), action.timestamp];
      return {
        ...state,
        error: action.error,
        errorTimes,
        loading: false
      };
    }
    case types.SCHEDULE_WARNINGS_RECEIVED: {
      let newState = { ...state };

      const { scheduleWarnings } = action.payload;
      const warningTypes = Object.keys(scheduleWarnings);

      for (const warningType of warningTypes) {
        const existingWarnings = state.scheduleWarnings[warningType] || [];
        const newWarnings = scheduleWarnings[warningType];

        newState = {
          ...newState,
          scheduleWarnings: {
            ...newState.scheduleWarnings,
            [warningType]: [...existingWarnings, ...newWarnings]
          }
        };
      }

      return newState;
    }
    case types.SCHEDULE_WARNINGS_CLEARED: {
      return { ...state, scheduleWarnings: {} };
    }
    case types.COMBINED_SCHEDULE_VALIDATION_ERRORS_RECEIVED: {
      const { id, ownerType, startTime, endTime, validationErrors } = action.payload;

      if (!state[ownerType]) {
        return state;
      }

      const schedule = state[ownerType][id];
      if (!schedule) return state;

      const intervals = (schedule.intervals || []).map(interval => {
        if (!overlaps(interval.start_time, interval.end_time, startTime, endTime)) return interval;

        return {
          ...interval,
          validationErrors
        };
      });

      return {
        ...state,
        [ownerType]: {
          ...state[ownerType],
          [id]: {
            ...schedule,
            intervals
          }
        }
      };
    }
    case types.START: {
      return { ...state, loading: true };
    }
    case types.SUCCESS: {
      return { ...state, loading: false, error: null, errorTimes: null };
    }
    // When receiving a schedule tick, it means that the time has progressed with
    // the minimal schedule increment (15 minutes). That means that the data we
    // have may already be stale.
    case 'SCHEDULE_TICK': {
      return { ...state, stale: checkStateStaleness(state) };
    }
    default: {
      return state;
    }
  }
};

export default combineReducers({
  full: fullScheduleReducer
});
